import React, { FC, useRef } from "react";
import { Object3D } from "three";
import { LODLevel, useLODLevel } from "./LODUtils";
import { useFrame } from "@react-three/fiber";
import { PERFORMANCE } from "./DevConstants";

interface LODModelProps {
  children: React.ReactNode;
  highDetailDistance?: number;
  mediumDetailDistance?: number;
  lowDetailDistance?: number;
}

/**
 * Component that applies Level of Detail (LOD) optimization to its children
 * based on distance from the camera
 */
export const LODModel: FC<LODModelProps> = ({
  children,
  highDetailDistance = PERFORMANCE.LOD.HIGH_DETAIL_DISTANCE,
  mediumDetailDistance = PERFORMANCE.LOD.MEDIUM_DETAIL_DISTANCE,
  lowDetailDistance = PERFORMANCE.LOD.LOW_DETAIL_DISTANCE,
}) => {
  const groupRef = useRef<Object3D>(null);
  const lodLevel = useLODLevel(groupRef.current);
  
  // Apply optimizations based on LOD level
  useFrame(() => {
    if (!groupRef.current) return;
    
    // Apply different optimizations based on LOD level
    switch (lodLevel) {
      case "HIGH":
        // No optimizations for high detail
        break;
      case "MEDIUM":
        // Medium detail optimizations
        break;
      case "LOW":
        // Low detail optimizations
        break;
    }
  });
  
  return (
    <group ref={groupRef}>
      {children}
    </group>
  );
};

/**
 * Component that conditionally renders its children based on LOD level
 */
interface ConditionalLODProps {
  children: React.ReactNode;
  minLevel: LODLevel;
  object: Object3D | null;
}

export const ConditionalLOD: FC<ConditionalLODProps> = ({
  children,
  minLevel,
  object,
}) => {
  const lodLevel = useLODLevel(object);
  
  // Determine if this component should render based on LOD level
  const shouldRender = () => {
    switch (minLevel) {
      case "HIGH":
        return lodLevel === "HIGH";
      case "MEDIUM":
        return lodLevel === "HIGH" || lodLevel === "MEDIUM";
      case "LOW":
        return true; // Render for all LOD levels
      default:
        return true;
    }
  };
  
  return shouldRender() ? <>{children}</> : null;
};